import { useState } from 'react'
import { api } from '../api.js'
import { useSessao } from '../sessao.jsx'
import Layout from '../componentes/Layout.jsx'
import {
  useDados, Campo, Erro, Aviso, Carregando, emDataHora,
} from '../componentes/basicos.jsx'

/**
 * Área do administrador (SPEC §3.2): só contas, nunca garagem alheia.
 * As travas do último admin vivem no servidor — aqui a interface apenas mostra
 * o erro que ele devolve, em vez de tentar adivinhar a regra.
 */
export default function Admin() {
  const { usuario } = useSessao()
  const usuarios = useDados('/admin/usuarios')
  const config = useDados('/admin/config')
  const eventos = useDados('/admin/eventos?limite=30')
  const [erro, setErro] = useState(null)

  async function agir(acao) {
    setErro(null)
    try {
      await acao()
      await Promise.all([usuarios.recarregar(), eventos.recarregar()])
    } catch (e) {
      setErro(e)
    }
  }

  // Só no primeiro carregamento: trocar a tela a cada recarga desmontava os
  // formulários e levava junto o que eles tinham acabado de mostrar.
  if (usuarios.carregando) return <Layout titulo="Administração"><Carregando /></Layout>

  return (
    <Layout titulo="Administração">
      <Erro erro={erro} />

      <div className="colunas-desktop">
      <Convidar recarregar={usuarios.recarregar} />

      <section className="cartao pilha largura-total">
        <span className="rotulo-secao">Contas</span>
        <ul className="lista">
          {(usuarios.dados?.usuarios ?? []).map((u) => (
            <li key={u.id}>
              <div className="pilha" style={{ gap: 8 }}>
                <div>
                  <div className="titulo-item">
                    {u.nome} {u.id === usuario.id && <span className="dica">(você)</span>}
                  </div>
                  <div className="detalhe">
                    {u.email} · {u.papel}
                    {u.arquivado && ' · arquivado'}
                    {u.provedores.length > 0 && ` · ${u.provedores.join(', ')}`}
                  </div>
                  <div className="detalhe">
                    último acesso: {u.ultimo_acesso_em ? emDataHora(u.ultimo_acesso_em) : 'nunca'}
                  </div>
                </div>

                <div className="linha">
                  <button type="button" className="botao-fantasma"
                          onClick={() => agir(() => api.patch(`/admin/usuarios/${u.id}/papel`, {
                            papel: u.papel === 'admin' ? 'comum' : 'admin',
                          }))}>
                    {u.papel === 'admin' ? 'Rebaixar' : 'Promover'}
                  </button>

                  <button type="button" className="botao-fantasma"
                          onClick={() => agir(() => api.post(`/admin/usuarios/${u.id}/revogar-sessoes`))}>
                    Revogar sessões
                  </button>

                  <BotaoConvite usuario={u} />

                  {u.arquivado ? (
                    <button type="button" className="botao-fantasma"
                            onClick={() => agir(() => api.post(`/admin/usuarios/${u.id}/desarquivar`))}>
                      Desarquivar
                    </button>
                  ) : (
                    <button type="button" className="botao-perigo"
                            onClick={() => agir(() => api.remover(`/admin/usuarios/${u.id}`))}>
                      Arquivar
                    </button>
                  )}
                </div>

                {u.arquivado && <Destruir usuario={u} recarregar={usuarios.recarregar} />}
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="cartao pilha">
        <span className="rotulo-secao">Cadastro</span>
        <label className="linha" style={{ gap: 8 }}>
          <input type="checkbox" style={{ width: 20, minHeight: 20 }}
                 checked={config.dados?.permitir_auto_cadastro ?? false}
                 onChange={async (e) => {
                   await api.put('/admin/config', { permitir_auto_cadastro: e.target.checked })
                   config.recarregar()
                 }} />
          <span>Permitir auto-cadastro por login social</span>
        </label>
        <p className="dica">
          Com isto ligado, quem entrar por Google ou Microsoft com e-mail verificado ganha
          uma conta comum e garagem vazia.
        </p>
      </section>

      <section className="cartao pilha">
        <span className="rotulo-secao">Atividade</span>
        <ul className="lista">
          {(eventos.dados?.eventos ?? []).map((e) => (
            <li key={e.id}>
              <div className="titulo-item">{e.tipo.replaceAll('_', ' ')}</div>
              <div className="detalhe">
                {emDataHora(e.criado_em)}
                {e.alvo && ` · ${e.alvo}`}
                {e.ator && ` · por ${e.ator}`}
              </div>
            </li>
          ))}
        </ul>
      </section>
      </div>
    </Layout>
  )
}

function Convidar({ recarregar }) {
  const [form, setForm] = useState({ nome: '', email: '', papel: 'comum' })
  const [convite, setConvite] = useState(null)
  const [erro, setErro] = useState(null)

  async function enviar(evento) {
    evento.preventDefault()
    setErro(null)
    try {
      const r = await api.post('/admin/usuarios', form)
      setConvite(r.convite)
      setForm({ nome: '', email: '', papel: 'comum' })
      await recarregar()
    } catch (e) {
      setErro(e)
    }
  }

  return (
    <form className="cartao pilha" onSubmit={enviar}>
      <span className="rotulo-secao">Convidar usuário</span>

      <Campo rotulo="Nome">
        <input value={form.nome} required onChange={(e) => setForm({ ...form, nome: e.target.value })} />
      </Campo>

      <Campo rotulo="E-mail">
        <input type="email" value={form.email} required
               onChange={(e) => setForm({ ...form, email: e.target.value })} />
      </Campo>

      <Campo rotulo="Papel">
        <select value={form.papel} onChange={(e) => setForm({ ...form, papel: e.target.value })}>
          <option value="comum">Comum</option>
          <option value="admin">Administrador</option>
        </select>
      </Campo>

      <Erro erro={erro} />

      <LinkConvite convite={convite} />

      <button type="submit" className="botao-principal botao-largo">Convidar</button>
    </form>
  )
}

/**
 * Resultado de um convite ou de uma redefinição de senha.
 *
 * O link só aparece quando é a única forma de entregá-lo: convite sem e-mail
 * configurado. Com e-mail, ele vai para a caixa do destinatário e para mais
 * ninguém; e a redefinição de uma conta em uso nunca volta para cá — com o link,
 * o admin poderia entrar como a pessoa e ver a garagem dela (SPEC §3.2).
 */
function LinkConvite({ convite }) {
  const [copiado, setCopiado] = useState(false)
  if (!convite) return null

  if (convite.tipo === 'redefinicao') {
    return convite.email_enviado
      ? <Aviso tipo="ok">Enviamos à pessoa, por e-mail, um link para redefinir a senha.</Aviso>
      : (
        <Aviso tipo="atencao">
          E-mail não configurado: o link de redefinição não chegou à pessoa. Configure o
          envio de e-mail no servidor e tente de novo.
        </Aviso>
      )
  }

  if (!convite.url) return <Aviso tipo="ok">Convite enviado por e-mail.</Aviso>

  return (
    <Aviso tipo="atencao">
      E-mail não configurado — envie este link você mesmo:
      <br />
      <code style={{ wordBreak: 'break-all' }}>{convite.url}</code>
      <br />
      <button type="button" style={{ minHeight: 36, marginTop: 8 }}
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(convite.url)
                  setCopiado(true)
                } catch {
                  setCopiado(false)
                }
              }}>
        {copiado ? 'Copiado' : 'Copiar link'}
      </button>
    </Aviso>
  )
}

/**
 * Convite pendente: reemite o convite (perdido ou vencido). Conta em uso: manda a
 * redefinição de senha ao e-mail do dono — o servidor decide, a tela só diz qual
 * dos dois vai acontecer.
 */
function BotaoConvite({ usuario }) {
  const [convite, setConvite] = useState(null)
  const [erro, setErro] = useState(null)

  if (usuario.arquivado) return null

  return (
    <>
      <button type="button" className="botao-fantasma"
              onClick={async () => {
                setErro(null)
                try {
                  const r = await api.post(`/admin/usuarios/${usuario.id}/convite`)
                  setConvite(r.convite)
                } catch (e) {
                  setErro(e)
                }
              }}>
        {usuario.convite_pendente ? 'Reenviar convite' : 'Redefinir senha'}
      </button>
      <Erro erro={erro} />
      <LinkConvite convite={convite} />
    </>
  )
}

/** Destruição definitiva: confirmação digitando o e-mail, como o servidor exige. */
function Destruir({ usuario, recarregar }) {
  const [aberto, setAberto] = useState(false)
  const [confirmacao, setConfirmacao] = useState('')
  const [erro, setErro] = useState(null)

  if (!aberto) {
    return (
      <button type="button" className="botao-perigo" onClick={() => setAberto(true)}>
        Destruir definitivamente
      </button>
    )
  }

  return (
    <div className="pilha">
      <Aviso tipo="erro">
        Isto apaga a conta e toda a garagem dela. Não há como desfazer.
      </Aviso>

      <Campo rotulo={`Digite ${usuario.email} para confirmar`}>
        <input value={confirmacao} onChange={(e) => setConfirmacao(e.target.value)} />
      </Campo>

      <Erro erro={erro} />

      <div className="linha">
        <button type="button" className="botao-perigo"
                disabled={confirmacao.trim().toLowerCase() !== usuario.email.toLowerCase()}
                onClick={async () => {
                  setErro(null)
                  try {
                    await api.remover(`/admin/usuarios/${usuario.id}/definitivo`, {
                      confirmacao_email: confirmacao,
                    })
                    await recarregar()
                  } catch (e) {
                    setErro(e)
                  }
                }}>
          Destruir
        </button>
        <button type="button" className="botao-fantasma" onClick={() => setAberto(false)}>Cancelar</button>
      </div>
    </div>
  )
}
